import './table-component.scss';
import { AuxiliaryMessageComponent } from '../auxiliary-message-component/auxiliary-message-component';
import { BiddingKeyboardComponent } from '../bidding-keybord-component/bidding-keyboard-component';
import {
    BridgeWarGameInfoComponent,
    BridgeWarGameSelectorComponent,
    BridgeWarInfosComponent,
    BridgeWarLogoComponent,
} from '../bridge-war/bridge-war-components';
import { Cards, SeatData, SeatPosition, Suit } from '../../app/types';
import { ChatComponent } from '../chat-component/chat-component';
import { ComponentContainer, ComponentContainerPosition } from '../../slices/tableSlice';
import { FooterComponent } from '../footer-component/footer-component';
import { SeatComponent } from '../seat-component/seat-component';
import { SimpleTricksCounterComponent } from '../simple-tricks-counter-component/simple-tricks-counter-component';
import { TableInfoComponent } from '../table-info-component/table-info-component';
import { TricksComponent } from '../tricks-component/tricks-component';
import { bridgeWarGui, environment } from '../../env';
import { classNames, getCardsOfSeat, getMaxCardsPerGroup } from '../../utils/mixed';
import { engineButtonClicked } from '../../app-interfaces/actions/engineCommands';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import AuctionBoxComponent from '../auction-box-component/auction-box-component';
import React, { CSSProperties, PropsWithChildren, useMemo } from 'react';
import TableSymbolComponent from '../table-symbol-component/table-symbol-component';
import TrickCounterComponent from '../trick-counter-component/trick-counter-component';

export const TableComponent: React.FC = () => {
    const dispatch = useAppDispatch();
    const { engineState } = useAppSelector((state) => state.app);
    const {
        bridgeTableHTML,
        bridgeTableVisibility,
        bridgeWarGameSelector,
        cardWidthMultiplier,
        centerSeat,
        contract,
        seatData,
        suitOrder,
        showTrickCounter,
        shouldCloseTrick,
        walkThrough,
        tableSymbol,
    } = useAppSelector((state) => state.table);
    const cardsState = useAppSelector((state) => state.cards);
    const maxCards = useMemo(() => {
        return [
            SeatPosition.top,
            SeatPosition.right,
            SeatPosition.bottom,
            SeatPosition.left,
        ].reduce<React.CSSProperties>((currCssVars, seatPosition) => {
            const bridgePosition = seatData[seatPosition].bridgePosition;
            if (bridgePosition) {
                const cards = getCardsOfSeat(cardsState, seatData[seatPosition].bridgePosition, contract, suitOrder);
                return {
                    ...currCssVars,
                    [`--max-cards-${seatPosition}`]: getMaxCardsPerGroup(cards),
                };
            }
            return currCssVars;
        }, {});
    }, [cardsState]);

    const mySeatData = [seatData.top, seatData.right, seatData.bottom, seatData.left].find(
        (_seatData) => _seatData.isMe,
    );

    const style = {
        '--tableBaseCardWidthMultiplier': cardWidthMultiplier,
        '--dummyVerticalMargin': walkThrough ? 20 : 5,
        ...[SeatPosition.top, SeatPosition.right, SeatPosition.bottom, SeatPosition.left].reduce<React.CSSProperties>(
            (cum, seatPosition) => {
                return {
                    ...cum,
                    [`--baseCardWidthMultiplier-${seatPosition}`]:
                        cardWidthMultiplier * seatData[seatPosition].cardWidthMultiplier,
                    [`--isMe-${seatPosition}`]: mySeatData?.seatPosition === seatPosition ? 1 : 0,
                    [`--verticalMaxCardsPerStack-${seatPosition}`]: getVerticalMaxCardsPerStack(
                        seatData[seatPosition],
                        cardsState,
                    ),
                };
            },
            {},
        ),

        '--center-top': 0.5,
        '--center-right': 0.5,
        '--center-bottom': 0.5,
        '--center-left': 0.5,

        '--cardOverlapH-top': environment === 'shark' ? 0.74 : 0.77,
        '--cardOverlapH-right': environment === 'shark' ? 0.74 : 0.77,
        '--cardOverlapH-bottom': environment === 'shark' ? 0.74 : 0.77,
        '--cardOverlapH-left': environment === 'shark' ? 0.74 : 0.77,

        '--cardOverlapV-top': 0.76,
        '--cardOverlapV-right': 0.76,
        '--cardOverlapV-bottom': 0.76,
        '--cardOverlapV-left': 0.76,

        ...maxCards,
    } as React.CSSProperties;
    //console.log("Re-Redner Table");
    return (
        <section
            className={classNames(
                'TableComponent',
                centerSeat && 'center-seat',
                !bridgeTableVisibility && 'bridge-table-hidden',
                bridgeWarGui && 'isBridgeWar',
                environment === 'shark' && 'isShark',
                [seatData.top, seatData.right, seatData.bottom, seatData.left]
                    .map((_seatData) => (_seatData.isVertical ? `isVertical-${_seatData.seatPosition}` : ''))
                    .join(' '),
            )}
            style={{ ...style }}
            onMouseDown={() => {
                shouldCloseTrick && dispatch(engineButtonClicked({ id: 'closeTrick', value: true }));
            }}
        >
            {shouldCloseTrick && (
                <div
                    className={classNames('close-trick')}
                    onMouseDown={() => {
                        dispatch(engineButtonClicked({ id: 'closeTrick', value: true }));
                    }}
                />
            )}

            {['topRight', 'topLeft', 'bottomRight', 'bottomLeft', 'center'].map((position) => (
                <CC key={position} component="test" position={position as ComponentContainerPosition}>
                    <div className={`test-div ${position}`} />
                </CC>
            ))}

            <SeatComponent seatPosition={SeatPosition.top} />
            {!centerSeat && (
                <>
                    <SeatComponent seatPosition={SeatPosition.right} />
                    <SeatComponent seatPosition={SeatPosition.bottom} />
                    <SeatComponent seatPosition={SeatPosition.left} />
                </>
            )}

            <TricksComponent />

            {showTrickCounter === 'audrey' && (
                <CC component="SimpleTricksCounterComponent">
                    <SimpleTricksCounterComponent />
                </CC>
            )}
            {showTrickCounter === 'shark' && (
                <CC component="TrickCounterComponent">
                    <TrickCounterComponent />
                </CC>
            )}
            {environment === 'shark' && (
                <CC component="BiddingKeyboardComponent">
                    <BiddingKeyboardComponent />
                </CC>
            )}

            <CC component="AuctionBoxComponent">
                <AuctionBoxComponent />
            </CC>

            <CC component="ChatComponent">
                <ChatComponent />
            </CC>

            <TableInfoComponent side="left" />
            <TableInfoComponent side="right" />
            {tableSymbol && (
                <CC component="TableSymbolComponent">
                    <TableSymbolComponent />
                </CC>
            )}
            <AuxiliaryMessages />
            {bridgeWarGameSelector && (
                <CC component="BridgeWarGameSelectorComponent" ccStyle={{ overflow: 'visible' }}>
                    <BridgeWarGameSelectorComponent />
                </CC>
            )}

            {bridgeTableHTML && (
                <div className="bridge-table-html" dangerouslySetInnerHTML={{ __html: bridgeTableHTML }} />
            )}

            {bridgeWarGui && (
                <>
                    <CC component="BridgeWarLogoComponent">
                        <BridgeWarLogoComponent />
                    </CC>
                    <CC component="BridgeWarInfosComponent">
                        <BridgeWarInfosComponent />
                    </CC>
                    <CC component="BridgeWarGameInfoComponent">
                        <BridgeWarGameInfoComponent seatData={mySeatData} />
                    </CC>
                    {engineState.service && <FooterComponent />}
                </>
            )}
        </section>
    );
};

function getVerticalMaxCardsPerStack(seatData: SeatData, cards: Cards) {
    if (!seatData.isVertical) {
        return 0;
    }
    const seatCardsBySuit = Object.values(cards)
        .filter((card) => card.bridgePosition === seatData.bridgePosition)
        .reduce<Record<Suit, number>>(
            (cumulated, card) => {
                return {
                    ...cumulated,
                    [card.suit]: cumulated[card.suit] + 1,
                };
            },
            {
                [Suit.clubs]: 0,
                [Suit.diamonds]: 0,
                [Suit.hearts]: 0,
                [Suit.spades]: 0,
            },
        );
    return Math.max(
        seatCardsBySuit[Suit.clubs],
        seatCardsBySuit[Suit.diamonds],
        seatCardsBySuit[Suit.hearts],
        seatCardsBySuit[Suit.spades],
    );
}

const CC: React.FC<
    PropsWithChildren & {
        component: ComponentContainer | 'AuxiliaryMessageComponent' | 'test';
        position?: ComponentContainerPosition;
        ccStyle?: CSSProperties;
    }
> = ({ component, position, children, ccStyle }) => {
    const { componentContainerPositions } = useAppSelector((state) => state.table);
    if (componentContainerPositions[component as ComponentContainer] === 'none') {
        return;
    }

    return (
        <div
            className={classNames(
                `componentContainer-${component}`,
                position ?? componentContainerPositions[component as ComponentContainer] ?? 'center',
            )}
            style={ccStyle}
        >
            {children}
        </div>
    );
};

const AuxiliaryMessages: React.FC = () => {
    const { auxiliaryMessages } = useAppSelector((state) => state.table);

    return (
        <>
            {auxiliaryMessages.center && (
                <CC component="AuxiliaryMessageComponent" position="center" ccStyle={{ overflow: 'visible' }}>
                    <AuxiliaryMessageComponent position="center" />
                </CC>
            )}
            {auxiliaryMessages.left && (
                <CC component="AuxiliaryMessageComponent" position="bottomLeft" ccStyle={{ overflow: 'visible' }}>
                    <AuxiliaryMessageComponent position="left" />
                </CC>
            )}
            {auxiliaryMessages.right && (
                <CC component="AuxiliaryMessageComponent" position="topRight" ccStyle={{ overflow: 'visible' }}>
                    <AuxiliaryMessageComponent position="right" />
                </CC>
            )}
        </>
    );
};
